"use client";

import { ArrowRight, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ScaleYourBrand() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const trustSignals = [{
    label: "UAE Registered"
  }, {
    label: "Industry Experts"
  }, {
    label: "Proven Results"
  }, {
    label: "Direct Access"
  }];
  const cityPairs = [{
    from: "YOUTUBE",
    to: "GROWTH"
  }, {
    from: "TIKTOK",
    to: "SCALE"
  }, {
    from: "INSTA",
    to: "REACH"
  }, {
    from: "BRAND",
    to: "EXPOSURE"
  }, {
    from: "CREATOR",
    to: "ACCESS"
  }, {
    from: "CAMPAIGN",
    to: "MANAGED"
  }];
  return <section data-section-id="824" id="scale-your-brand" className="relative bg-background text-foreground overflow-hidden min-h-[720px] lg:h-screen flex items-stretch">
      <div className="grid grid-cols-1 lg:grid-cols-12 w-full">
        {}
        <div ref={leftRef} className={`lg:col-span-7 relative flex flex-col justify-between px-6 py-12 sm:px-10 sm:py-16 lg:px-20 lg:py-20 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs sm:text-sm tracking-widest uppercase text-foreground/80 flex items-center gap-2"><span className="inline-block w-1.5 h-1.5 rounded-lg bg-[var(--chart-1)]" data-ast-id="f:/src/pages/Home/sections/ScaleYourBrand.tsx|sig:t81mxm" data-ast-start="1725" data-ast-end="1801"></span>PINE PLAYERZ / UAE REGISTERED / GLOBAL</span>
            </div>
            <div className="h-px w-24 bg-border" />
          </div>

          {}
          <div className="mt-10 lg:mt-12">
            <h1 className="font-default font-extrabold text-foreground leading-[0.92]" style={{
            fontSize: "clamp(3rem, 9vw, 9rem)",
            letterSpacing: "-0.045em"
          }}>
              <span className="block">Scale your</span>
              <span className="block">brand through</span>
              <span className="block relative inline-block">
                <span className="relative inline-block">influencer marketing.<svg className="absolute left-0 -bottom-2 w-full" viewBox="0 0 300 24" fill="none" preserveAspectRatio="none" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/ScaleYourBrand.tsx|sig:jytzsg" data-ast-start="2563" data-ast-end="3361"><path d="M2 16 C 60 4, 140 22, 220 10 S 290 14, 298 8" stroke="var(--chart-1)" stroke-width="5" stroke-linecap="round" fill="none" data-ast-id="f:/src/pages/Home/sections/ScaleYourBrand.tsx|sig:8xovs0" data-ast-start="2832" data-ast-end="3336" style={{
                    strokeDasharray: '600',
                    strokeDashoffset: '600',
                    animation: 'drawLine 1.4s ease-out 0.6s forwards, fadeOut 4s ease-out forwards'
                  }}></path></svg></span>
              </span>
            </h1>

            <p className="mt-8 max-w-[480px] text-base sm:text-lg text-foreground/70 leading-relaxed">
              Helping brands reach millions through strategic influencer marketing. We connect your vision with the right creators to drive real growth.
            </p>

            {}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg px-7 h-12 text-base font-semibold bg-[var(--chart-1)] text-primary-foreground hover:bg-[var(--chart-1)]/90 shadow-md">
                <Link to="#contact-us">
                  Contact Us
                  <ArrowRight className="ml-1 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-lg px-7 h-12 text-base font-semibold border-2 border-foreground text-foreground bg-transparent hover:bg-foreground hover:text-background">
                <Link to="#what-we-do">Our Services</Link>
              </Button>
            </div>
          </div>

          {}
          <div className="mt-12 lg:mt-16 pt-6 border-t border-border">
            <div className="flex items-center gap-3 sm:gap-5 overflow-x-auto whitespace-nowrap font-mono text-xs sm:text-sm uppercase tracking-wider text-foreground/70 -mx-2 px-2">
              {trustSignals.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-3 sm:gap-5 shrink-0">
                  {i > 0 && <span className="w-1.5 h-1.5 rounded-lg bg-[var(--chart-1)]" />}
                  <span>{item.label}</span>
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`lg:col-span-5 relative min-h-[70vh] lg:min-h-0 transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/influencer_marketing_strategy_dashboard.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/influencer_marketing_strategy_dashboard.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/influencer_marketing_strategy_dashboard.webp-wvc-sizes" alt="Pine Playerz Media influencer campaign strategy dashboard" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

          {}
          <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 max-w-[300px] bg-foreground text-background p-5 rounded-md shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="size-4 text-[var(--chart-1)]" />
              <span className="font-mono text-[10px] tracking-widest uppercase text-background/60">
                Live Campaign
              </span>
            </div>

            {}
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-lg bg-background" />
              <div className="flex-1 h-px bg-background/40 relative">
                <div className="absolute inset-0 h-px bg-[var(--chart-1)] origin-left animate-pulse" />
              </div>
              <span className="w-2 h-2 rounded-lg bg-[var(--chart-1)]" />
            </div>

            <p className="font-mono text-sm sm:text-base tracking-tight text-background mb-3">
              CREATOR REACH → BRAND GROWTH
            </p>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-background/10 border border-background/20">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-lg bg-[var(--chart-1)] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-lg size-2 bg-[var(--chart-1)]" />
              </span>
              <span className="font-mono text-[10px] tracking-widest uppercase text-background">
                Active
              </span>
            </div>
          </div>

          {}
          <div className="hidden lg:flex absolute top-0 right-0 h-full w-10 bg-foreground items-center justify-center overflow-hidden">
            <div className="flex flex-col gap-6 font-mono text-[11px] tracking-widest uppercase text-background/80" style={{
            writingMode: "vertical-rl",
            animation: "haulx-ticker 30s linear infinite"
          }}>
              {[...cityPairs, ...cityPairs].map((pair, i) => <span key={i} data-index={i} className="whitespace-nowrap">
                  {pair.from} → {pair.to}
                </span>)}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes haulx-ticker {
          0% { transform: translateY(-50%); }
          100% { transform: translateY(50%); }
        }
        @keyframes drawLine {
          from { stroke-dashoffset: 600; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes fadeOut {
          0% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </section>;
}